/**
 * Marketplace API client.
 *
 * For local dev, Vite proxies /api → http://localhost:8787 (see vite.config.ts).
 * In prod, the SPA is on mp.langgenius.app and the worker on api.mp.langgenius.app,
 * so the absolute base flips to the api host. CORS on the worker allows the SPA
 * origin with credentials.
 */
import { CurrentUser, type CurrentUser as CurrentUserT } from '@arx-marketplace/shared/auth';
import {
  TalentDetail,
  TalentSummary,
  type CreateTalentBody,
  type ListTalentsQuery,
  type TalentDetail as TalentDetailT,
  type TalentSummary as TalentSummaryT,
  type UpdateTalentBody,
} from '@arx-marketplace/shared/talents';
import type { CreateShowcaseBody, UpdateShowcaseBody } from '@arx-marketplace/shared/showcase';
import {
  TulpaDetail,
  TulpaSummary,
  type CreateTulpaBody,
  type ListTulpasQuery,
  type TulpaDetail as TulpaDetailT,
  type TulpaSummary as TulpaSummaryT,
  type UpdateTulpaBody,
} from '@arx-marketplace/shared/tulpas';

// API + SPA are served from the same Worker in prod (mp.langgenius.app);
// in local dev Vite proxies /api → wrangler dev. Either way: relative URLs.
const API_BASE = '';

export interface HealthStatus {
  status: string;
  env: string;
  db: string;
  ts: string;
}

export interface PageEnvelope<T> {
  items: T[];
  page: number;
  per_page: number;
  total: number;
}

async function jsonRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers as Record<string, string>) },
    ...init,
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`api ${res.status} ${path}: ${detail}`);
  }
  return (await res.json()) as T;
}

export const apiClient = {
  // Auth ---------------------------------------------------------------
  async health(): Promise<HealthStatus> {
    return jsonRequest<HealthStatus>('/api/health');
  },
  async me(): Promise<CurrentUserT | null> {
    const raw = await jsonRequest<{ user: unknown }>('/api/auth/me');
    if (raw.user == null) return null;
    return CurrentUser.parse(raw.user);
  },
  async logout(): Promise<void> {
    await fetch(`${API_BASE}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  },
  loginGithubUrl(): string {
    return `${API_BASE}/api/auth/github`;
  },
  loginGoogleUrl(): string {
    return `${API_BASE}/api/auth/google`;
  },

  // Talents ------------------------------------------------------------
  async listTalents(q: Partial<ListTalentsQuery> = {}): Promise<PageEnvelope<TalentSummaryT>> {
    const sp = new URLSearchParams();
    if (q.q) sp.set('q', q.q);
    if (q.category) sp.set('category', q.category);
    if (q.page) sp.set('page', String(q.page));
    if (q.per_page) sp.set('per_page', String(q.per_page));
    const qs = sp.toString() ? `?${sp.toString()}` : '';
    const raw = await jsonRequest<PageEnvelope<unknown>>(`/api/talents${qs}`);
    return { ...raw, items: raw.items.map((i) => TalentSummary.parse(i)) };
  },
  async getTalent(slug: string): Promise<TalentDetailT> {
    const raw = await jsonRequest<unknown>(`/api/talents/${encodeURIComponent(slug)}`);
    return TalentDetail.parse(raw);
  },
  async createTalent(body: CreateTalentBody): Promise<{ id: string; slug: string }> {
    return jsonRequest('/api/talents', { method: 'POST', body: JSON.stringify(body) });
  },
  async updateTalent(slug: string, body: UpdateTalentBody): Promise<void> {
    await jsonRequest(`/api/talents/${encodeURIComponent(slug)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },
  async deleteTalent(slug: string): Promise<void> {
    await jsonRequest(`/api/talents/${encodeURIComponent(slug)}`, { method: 'DELETE' });
  },
  async uploadVersion(slug: string, file: File): Promise<{ id: string; version: string }> {
    const form = new FormData();
    form.append('bundle', file);
    const res = await fetch(`${API_BASE}/api/talents/${encodeURIComponent(slug)}/versions`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(`upload ${res.status}: ${detail}`);
    }
    return (await res.json()) as { id: string; version: string };
  },
  downloadUrl(slug: string, version?: string): string {
    const vQuery = version ? `?version=${encodeURIComponent(version)}` : '';
    return `${API_BASE}/api/talents/${encodeURIComponent(slug)}/download${vQuery}`;
  },

  // Showcase -----------------------------------------------------------
  async createShowcase(slug: string, body: CreateShowcaseBody): Promise<{ id: string }> {
    return jsonRequest(`/api/talents/${encodeURIComponent(slug)}/showcase`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  async updateShowcase(id: string, body: UpdateShowcaseBody): Promise<void> {
    await jsonRequest(`/api/showcase/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },
  async deleteShowcase(id: string): Promise<void> {
    await jsonRequest(`/api/showcase/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },

  // Tulpas -------------------------------------------------------------
  async listTulpas(q: Partial<ListTulpasQuery> = {}): Promise<PageEnvelope<TulpaSummaryT>> {
    const sp = new URLSearchParams();
    if (q.q) sp.set('q', q.q);
    if (q.engine) sp.set('engine', q.engine);
    if (q.page) sp.set('page', String(q.page));
    if (q.per_page) sp.set('per_page', String(q.per_page));
    const qs = sp.toString() ? `?${sp.toString()}` : '';
    const raw = await jsonRequest<PageEnvelope<unknown>>(`/api/tulpas${qs}`);
    return { ...raw, items: raw.items.map((i) => TulpaSummary.parse(i)) };
  },
  async getTulpa(slug: string): Promise<TulpaDetailT> {
    const raw = await jsonRequest<unknown>(`/api/tulpas/${encodeURIComponent(slug)}`);
    return TulpaDetail.parse(raw);
  },
  async createTulpa(body: CreateTulpaBody): Promise<{ id: string; slug: string }> {
    return jsonRequest('/api/tulpas', { method: 'POST', body: JSON.stringify(body) });
  },
  async updateTulpa(slug: string, body: UpdateTulpaBody): Promise<void> {
    await jsonRequest(`/api/tulpas/${encodeURIComponent(slug)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },
  async deleteTulpa(slug: string): Promise<void> {
    await jsonRequest(`/api/tulpas/${encodeURIComponent(slug)}`, { method: 'DELETE' });
  },
  async uploadTulpaVersion(slug: string, file: File): Promise<{ id: string; version: string }> {
    const form = new FormData();
    form.append('bundle', file);
    const res = await fetch(`${API_BASE}/api/tulpas/${encodeURIComponent(slug)}/versions`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(`upload ${res.status}: ${detail}`);
    }
    return (await res.json()) as { id: string; version: string };
  },
  tulpaDownloadUrl(slug: string, version?: string): string {
    const vQuery = version ? `?version=${encodeURIComponent(version)}` : '';
    return `${API_BASE}/api/tulpas/${encodeURIComponent(slug)}/download${vQuery}`;
  },
};
